import { type ReactNode, useEffect, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

import { ModalCloseButton } from "@/components/shared/ModalCloseButton";
import { Button } from "@/components/ui/button";

// Swipe-to-close tuning (measured in px within the modal panel).
const SWIPE_CLOSE_MIN_DELTA_Y_PX = 50;
const SWIPE_CLOSE_MAX_START_Y_PX = 100;

export interface MobileModalSection {
  id: string;
  title: string;
  content: ReactNode;
  defaultOpen?: boolean;
  showDivider?: boolean;
  /** Optional one-line preview shown when the section is collapsed. */
  previewText?: ReactNode;
}

export interface MobileModalContent {
  productImage?: ReactNode;
  sections?: MobileModalSection[];
  primaryButton?: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  secondaryButton?: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
  };
}

export interface MobileModalScaffoldProps {
  mobileContent?: MobileModalContent;
  children?: ReactNode;
  showCloseButton?: boolean;
  onClose: () => void;
  /** Enables a simple "swipe down near the top to close" gesture. */
  enableSwipeToClose?: boolean;
  /** Space reserved above the sticky CTA area so content isn't obscured. */
  bottomSpacerClassName?: string;
}

/**
 * Standard mobile modal layout used across shopping flows:
 * - drag handle
 * - optional close button
 * - scrollable content with collapsible sections
 * - sticky bottom CTA(s)
 *
 * NOTE: This component does NOT decide "full-screen" vs "bottom sheet".
 * That is controlled by the modal shell/container (e.g. `BasicModalShell` full-height vs
 * `SheetModalShell` bottom sheet).
 */
export function MobileModalScaffold({
  mobileContent,
  children,
  showCloseButton = true,
  onClose,
  enableSwipeToClose = true,
  bottomSpacerClassName = "h-24",
}: MobileModalScaffoldProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [touchStart, setTouchStart] = useState<{
    /** viewport-relative */
    y: number;
    /** modal-relative (distance from top of modal panel) */
    yWithinModal: number;
  } | null>(null);
  const didSwipeCloseRef = useRef(false);

  const sectionsKey = (mobileContent?.sections ?? [])
    .map((s) => `${s.id}:${s.defaultOpen ? 1 : 0}`)
    .join("|");
  const didInitSectionsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (didInitSectionsKeyRef.current === sectionsKey) return;
    didInitSectionsKeyRef.current = sectionsKey;
    const sections = mobileContent?.sections ?? [];
    if (sections.length === 0) return;
    setOpenSections(new Set(sections.filter((s) => s.defaultOpen).map((s) => s.id)));
  }, [mobileContent?.sections, sectionsKey]);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enableSwipeToClose) return;
    didSwipeCloseRef.current = false;
    const touch = e.touches[0];
    const rect = containerRef.current?.getBoundingClientRect();
    const yWithinModal = rect ? touch.clientY - rect.top : touch.clientY;
    setTouchStart({ y: touch.clientY, yWithinModal });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!enableSwipeToClose) return;
    if (didSwipeCloseRef.current) return;
    if (!touchStart) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - touchStart.y;

    // If swiping down enough from the top area, close modal.
    if (
      deltaY > SWIPE_CLOSE_MIN_DELTA_Y_PX &&
      touchStart.yWithinModal < SWIPE_CLOSE_MAX_START_Y_PX
    ) {
      didSwipeCloseRef.current = true;
      setTouchStart(null);
      onClose();
    }
  };

  const handleTouchEnd = () => {
    didSwipeCloseRef.current = false;
    setTouchStart(null);
  };

  if (!mobileContent) {
    return <div className="h-full p-6">{children}</div>;
  }

  const { productImage, sections = [], primaryButton, secondaryButton } = mobileContent;

  return (
    <div ref={containerRef} className="flex h-full flex-col">
      {/* Drag Handle */}
      <div className="flex justify-center pb-2 pt-3">
        <div className="h-1 w-12 rounded-full bg-gray-300" />
      </div>

      {/* Close Button - Top Right */}
      {showCloseButton && (
        <ModalCloseButton onClick={onClose} className="absolute right-4 top-4 z-20" />
      )}

      {/* Scrollable Content */}
      <div
        className="flex-1 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Product Image Section */}
        {productImage && <div className="bg-gray-50/30 px-6 py-4">{productImage}</div>}

        {/* Dynamic Sections */}
        <div className="px-6">
          {sections.map((section, index) => {
            const isSectionOpen = openSections.has(section.id);

            return (
              <div key={section.id} className="py-4">
                {/* Section Divider */}
                {section.showDivider && index > 0 && (
                  <div className="-mx-6 mb-6 border-b border-gray-200" />
                )}

                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="group -mx-2 flex w-full items-center justify-between rounded-lg px-2 py-3 text-left transition-colors hover:bg-gray-50"
                  type="button"
                >
                  <div className="flex items-center gap-3">
                    <h3 className="text-xs font-medium uppercase tracking-widest text-gray-900">
                      {section.title}
                    </h3>
                  </div>
                  <ChevronRight
                    className={`h-[14px] w-[14px] flex-shrink-0 text-gray-500 transition-transform duration-200 ${
                      isSectionOpen ? "rotate-90" : ""
                    }`}
                  />
                </button>

                {/* Section Content */}
                <AnimatePresence>
                  {isSectionOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pb-2 pt-4">
                        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                          {section.content}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Always-visible preview for closed sections */}
                {!isSectionOpen && section.previewText && (
                  <div className="pb-1 pt-2">
                    <div className="line-clamp-1 text-sm text-gray-500">{section.previewText}</div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Fallback content if no sections provided */}
          {sections.length === 0 && (
            <div className="py-6 text-base leading-relaxed">{children}</div>
          )}

          {/* Bottom padding for sticky buttons */}
          <div className={bottomSpacerClassName} />
        </div>
      </div>

      {/* Sticky Bottom Buttons */}
      {(primaryButton || secondaryButton) && (
        <div className="sticky bottom-0 border-t border-gray-100 bg-white p-4">
          <div className="mx-auto max-w-sm">
            <div className="flex gap-3 max-[360px]:flex-col">
              {primaryButton && (
                <Button
                  onClick={primaryButton.onClick}
                  disabled={primaryButton.disabled || primaryButton.loading}
                  aria-busy={primaryButton.loading}
                  data-loading={primaryButton.loading ? "true" : "false"}
                  className="flex-1 border-0 bg-gray-900 py-4 text-base font-medium text-white shadow-none hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {primaryButton.text}
                </Button>
              )}
              {secondaryButton && (
                <Button
                  variant="outline"
                  onClick={secondaryButton.onClick}
                  disabled={secondaryButton.disabled}
                  className="flex-none border-gray-300 bg-white px-6 py-4 text-base font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 max-[360px]:flex-1"
                >
                  {secondaryButton.text}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
